const DX_TRIALS_SIGNUP_PATH = "/api/proxy/api/v1/dx-trials/signup";

export type DxTrialSignupResponse = {
  trialId: string;
  organizationId: string;
  teamId: string;
};

const SUPPORT_NOTIFIED_HEADER = "X-Trial-Support-Notified";

export class DxTrialSignupError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    // Only the API knows whether it paged support; never infer it from the status.
    readonly supportNotified: boolean = false,
  ) {
    super(`DX Trial signup failed with status ${status}`);
    this.name = "DxTrialSignupError";
  }
}

export type DxTrialAttribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

export async function createDxTrialSignup(input: {
  companyName: string;
  companyRole?: string;
  // Optional to match CreateDxTrialSignupDto: a handoff that lost the phone must
  // still provision rather than block the visitor at the last step.
  phone?: string;
  acceptedTerms: true;
  idToken: string;
  accessToken: string;
  attribution?: DxTrialAttribution;
}): Promise<DxTrialSignupResponse> {
  const response = await fetch(DX_TRIALS_SIGNUP_PATH, {
    method: "POST",
    cache: "no-store",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${input.idToken}`,
      "X-Trimble-Access-Token": `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify({
      companyName: input.companyName,
      companyRole: input.companyRole,
      phone: input.phone,
      acceptedTerms: input.acceptedTerms,
      ...input.attribution,
    }),
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new DxTrialSignupError(response.status, body, response.headers.get(SUPPORT_NOTIFIED_HEADER) === "true");
  }
  if (!isDxTrialSignupResponse(body)) {
    throw new Error("DX Trial signup returned an invalid response");
  }
  return body;
}

function isDxTrialSignupResponse(value: unknown): value is DxTrialSignupResponse {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.trialId === "string" &&
    typeof candidate.organizationId === "string" &&
    typeof candidate.teamId === "string"
  );
}

const DX_TRIALS_LEAD_PATH = "/api/proxy/api/v1/dx-trial-leads";

export type DxTrialLead = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  country?: string;
  state?: string;
  marketingOptIn?: boolean;
  acceptedTerms: true;
  turnstileToken: string;
};

export class DxTrialLeadError extends Error {
  constructor(readonly status: number) {
    super(`DX Trial lead capture failed with status ${status}`);
    this.name = "DxTrialLeadError";
  }
}

export async function createDxTrialLead(lead: DxTrialLead): Promise<void> {
  const response = await fetch(DX_TRIALS_LEAD_PATH, {
    method: "POST",
    cache: "no-store",
    credentials: "include",
    headers: { "Content-Type": "application/vnd.api+json" },
    body: JSON.stringify({
      data: {
        type: "dx-trial-leads",
        attributes: lead,
      },
    }),
  });
  if (!response.ok) {
    throw new DxTrialLeadError(response.status);
  }
}
